import type { ChipProps } from "@mui/material";

import {
  CustomFieldRecord,
  CustomFieldStatus,
  CustomFieldType,
  TargetEntityType,
} from "@/shared/domain/customFields";

/** Keys understood by the shared `FieldTypeIcon` component. */
type FieldTypeIconKey = "string" | "numeric" | "date" | "boolean" | "enum";

// Each map is keyed by its full enum, so a new enum member is a compile error
// here until its presentation is filled in.

/** Traffic-light color per lifecycle status: green usable / red failed / amber in-flight. */
const STATUS_COLORS: Record<CustomFieldStatus, ChipProps["color"]> = {
  [CustomFieldStatus.Ready]: "success",
  [CustomFieldStatus.Failed]: "error",
  [CustomFieldStatus.Creating]: "warning",
  [CustomFieldStatus.Deleting]: "warning",
};

const STATUS_LABELS: Record<CustomFieldStatus, string> = {
  [CustomFieldStatus.Creating]: "Creating",
  [CustomFieldStatus.Ready]: "Ready",
  [CustomFieldStatus.Deleting]: "Deleting",
  [CustomFieldStatus.Failed]: "Failed",
};

// Reuses the shared filter-UI icons: `Timestamp` has no dedicated icon so it
// shares `date`, and `Number` maps to `numeric`.
const TYPE_ICON_KEYS: Record<CustomFieldType, FieldTypeIconKey> = {
  [CustomFieldType.String]: "string",
  [CustomFieldType.Number]: "numeric",
  [CustomFieldType.Timestamp]: "date",
  [CustomFieldType.Boolean]: "boolean",
  [CustomFieldType.Enum]: "enum",
};

const TYPE_LABELS: Record<CustomFieldType, string> = {
  [CustomFieldType.String]: "String",
  [CustomFieldType.Number]: "Number",
  [CustomFieldType.Timestamp]: "Timestamp",
  [CustomFieldType.Boolean]: "Boolean",
  [CustomFieldType.Enum]: "Enum",
};

export function customFieldStatusColor(
  status: CustomFieldStatus,
): ChipProps["color"] {
  return STATUS_COLORS[status];
}

export function customFieldStatusLabel(status: CustomFieldStatus): string {
  return STATUS_LABELS[status];
}

export function customFieldTypeIconType(
  type: CustomFieldType,
): FieldTypeIconKey {
  return TYPE_ICON_KEYS[type];
}

export function customFieldTypeLabel(type: CustomFieldType): string {
  return TYPE_LABELS[type];
}

/** Label shown for a field, defaulting to the field name when no display name is set. */
export function customFieldDisplayName(field: CustomFieldRecord): string {
  return field.display_name ?? field.field_name;
}

/** Lowercase plural noun for an entity type, for inline prose (e.g. quota copy). */
const ENTITY_TYPE_PLURALS: Record<TargetEntityType, string> = {
  [TargetEntityType.Collection]: "collections",
  [TargetEntityType.Dataset]: "datasets",
  [TargetEntityType.Device]: "devices",
  [TargetEntityType.Event]: "events",
  [TargetEntityType.Session]: "sessions",
};

export function customFieldEntityTypePlural(
  entityType: TargetEntityType,
): string {
  return ENTITY_TYPE_PLURALS[entityType];
}

/** Singular capitalized noun for an entity type, e.g. for the create dialog title. */
const ENTITY_TYPE_LABELS: Record<TargetEntityType, string> = {
  [TargetEntityType.Collection]: "Collection",
  [TargetEntityType.Dataset]: "Dataset",
  [TargetEntityType.Device]: "Device",
  [TargetEntityType.Event]: "Event",
  [TargetEntityType.Session]: "Session",
};

export function customFieldEntityTypeLabel(
  entityType: TargetEntityType,
): string {
  return ENTITY_TYPE_LABELS[entityType];
}

/**
 * Whether a field can be edited. Only `Ready` fields qualify — this matches the
 * backend update guard (it updates `WHERE status = 'ready'`), so editing a
 * Creating/Deleting/Failed field would be rejected server-side.
 */
export function isCustomFieldEditable(status: CustomFieldStatus): boolean {
  return status === CustomFieldStatus.Ready;
}
